import { useFormik } from 'formik';
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { Form, Label, Loader } from '@/components';
import {
  InputAutoComplete,
  ItemAutocomplete,
} from '@/components/InputAutocomplete';
import { TabType } from '@/components/Types';
import { PropsWithClassName } from '@/types';
import {
  oneSpaceBetweenWordMinTwoChar,
  searchTourneySchema,
  validateFieldError,
} from '@/utils';

interface Props extends PropsWithChildren, PropsWithClassName {
  placeholder: string;
  label?: string;
  onHandleRequest: (value: { valueSearch: string; page: number }) => void;
  onHandleKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  listSuggested?: ItemAutocomplete[];
  sendForm: boolean;
  autocomplete?: boolean;
  onHandleCleanResult: () => void;
  listSuggestionsAutocomplete?: (term: string) => void;
  tabSelected: TabType;
  onHandleTabSelected: (tab: TabType) => void;
  disabled?: boolean;
}

export const TourneySearch = React.memo(
  ({
    placeholder,
    className,
    label,
    onHandleRequest,
    onHandleKeyUp,
    listSuggested,
    listSuggestionsAutocomplete,
    onHandleCleanResult,
    autocomplete,
    sendForm,
    tabSelected,
    disabled,
    onHandleTabSelected,
  }: Props) => {
    const inputRef = useRef(null);

    const {
      setFieldValue,
      handleSubmit,
      values,
      isSubmitting,
      handleBlur,
      touched,
      errors,
    } = useFormik({
      initialValues: {
        valueSearch: '',
      },
      validateOnMount: false,
      validateOnBlur: true,
      validateOnChange: true,
      validationSchema: searchTourneySchema,
      onSubmit: async (val: { valueSearch: string }, { setSubmitting }) => {
        if (val.valueSearch.length >= 2) {
          onHandleRequest({ valueSearch: val.valueSearch, page: 1 });
          setSubmitting(false);
        }
      },
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = oneSpaceBetweenWordMinTwoChar(e.target.value);

      if (value.length < 2) {
        onHandleTabSelected(TabType.FirstTab);
        onHandleCleanResult();
      }

      setFieldValue('valueSearch', value);
      listSuggestionsAutocomplete && listSuggestionsAutocomplete(value);
    };

    // funcion de reseteo de formulario y resultado
    const onHandleClearField = useCallback(() => {
      onHandleCleanResult();
      setFieldValue('valueSearch', '');
      onHandleTabSelected(TabType.FirstTab);
    }, [onHandleCleanResult, onHandleTabSelected, setFieldValue]);

    useEffect(() => {
      if (sendForm) handleSubmit();
    }, [handleSubmit, sendForm, tabSelected]);

    return (
      <div className={className}>
        {label && <Label className="font-bold text-base mb-4">{label}</Label>}
        <Loader isVisible={isSubmitting} />
        <Form.Form
          loading={isSubmitting}
          onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <InputAutoComplete
            ref={inputRef}
            disabled={disabled}
            error={validateFieldError(
              touched.valueSearch,
              errors.valueSearch,
              values.valueSearch.length
            )}
            handleCancel={onHandleClearField}
            handleInputSelect={(value: string) => {
              if (autocomplete) {
                if (value === null) return;
                setFieldValue('valueSearch', value);
                handleSubmit();
              }
            }}
            list={listSuggested || []}
            name="search-tda-dts"
            placeholder={placeholder}
            value={values.valueSearch}
            onBlur={handleBlur}
            onChange={handleInputChange}
            onKeyUp={onHandleKeyUp}
            onSubmit={() => handleSubmit()}
          />
        </Form.Form>
      </div>
    );
  }
);

TourneySearch.displayName = 'TourneySearch';
